import { normalizeFrenchLanguageCode } from '@/utils/languageCode';
import { toReaderLocale } from '@/utils/numberFormat';

export interface ManagerLabelSources {
    /** The reader's current language code. */
    language: string;
    /** The company default, read when the reader's own language has no configured word. */
    defaultLanguage?: string;
    identifiers?: Record<string, string>;
    pluralIdentifiers?: Record<string, string>;
    /** The words cached on the session user, for a reader with no company in context. */
    userLabel?: string;
    userPluralLabel?: string;
    translate: (key: 'common.manager' | 'common.managers') => string;
}

export interface ManagerLabels {
    managerLabel: string;
    managerPluralLabel: string;
    /**
     * The same words with a raised first letter, for a label that stands on its own — a column
     * header, a tab, a filter name. A sentence takes the plain ones; only the head of a standalone
     * label takes these, and the dictionary says which by the variable it carries.
     */
    managerLabelCapitalized: string;
    managerPluralLabelCapitalized: string;
}

/**
 * A company stores its word for the role once per language code, so the reader's own locale comes
 * first, then its normalized French variant, then the company default.
 */
const configured = (
    identifiers: Record<string, string> | undefined,
    language: string,
    defaultLanguage: string | undefined,
): string | undefined =>
    identifiers?.[language] ||
    identifiers?.[normalizeFrenchLanguageCode(language)] ||
    (defaultLanguage ? identifiers?.[defaultLanguage] : undefined);

/**
 * The dictionary entry is the only source to fold: the configured ones are seeded in lower case,
 * while `common.manager` is not, because it also stands alone as a label elsewhere. Folded in the
 * reader's own locale, and never applied to the word a company chose — "DRH" stays "DRH".
 */
const midSentence = (dictionaryWord: string, language: string): string =>
    dictionaryWord.toLocaleLowerCase(toReaderLocale(language));

const capitalize = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * The singular and the plural of the word a company uses for the role, resolved through one chain
 * so that changing its order changes it once. The plural is read, never derived: no rule turns a
 * word we do not know into its plural.
 */
export const resolveManagerLabels = ({
    language,
    defaultLanguage,
    identifiers,
    pluralIdentifiers,
    userLabel,
    userPluralLabel,
    translate,
}: ManagerLabelSources): ManagerLabels => {
    const managerLabel =
        configured(identifiers, language, defaultLanguage) ||
        userLabel ||
        midSentence(translate('common.manager'), language);
    const managerPluralLabel =
        configured(pluralIdentifiers, language, defaultLanguage) ||
        userPluralLabel ||
        midSentence(translate('common.managers'), language);

    return {
        managerLabel,
        managerPluralLabel,
        managerLabelCapitalized: capitalize(managerLabel),
        managerPluralLabelCapitalized: capitalize(managerPluralLabel),
    };
};
